import type {
  OpenPostPosting,
  OpenPostPostingWrapper,
} from '@festina/life-general-types/life'
import {
  type Except,
  type Merge,
  notEmpty,
  type SetRequiredAndNonNullable,
} from '@festina-life/core'
import startCase from 'lodash-es/startCase'

import type { BalancePostingWrapperWithSum } from '../balancePostTable/types'

/**
 * Formats object keys for display and recursively removes null values and empty containers.
 */
export const cleanUpTechnicalData = (item: unknown): unknown => {
  if (Array.isArray(item)) {
    const cleanedArray = item
      .map(cleanUpTechnicalData)
      .filter((value) => value !== null)

    return cleanedArray.length > 0 ? cleanedArray : null
  }

  if (item !== null && typeof item === 'object') {
    const cleanedObject: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(item)) {
      const cleanedValue = cleanUpTechnicalData(value)

      if (cleanedValue !== null) {
        cleanedObject[startCase(key)] = cleanedValue
      }
    }

    return Object.keys(cleanedObject).length > 0 ? cleanedObject : null
  }

  return item
}

export const hasBalancePostingVoucherRef = (
  row: BalancePostingWrapperWithSum,
): row is Merge<
  BalancePostingWrapperWithSum,
  {
    balancePosting: SetRequiredAndNonNullable<
      NonNullable<BalancePostingWrapperWithSum['balancePosting']>,
      'voucherRef'
    >
  }
> => notEmpty(row.balancePosting?.voucherRef)

type OpenPostPostingWithSumRow = Omit<OpenPostPosting, '_type'> & {
  _type: 'sumRow' | OpenPostPosting['_type']
}

export type OpenPostPostingWrapperWithSum = Except<
  OpenPostPostingWrapper,
  'openPostPosting'
> & {
  openPostPosting?: OpenPostPostingWithSumRow | null
}

export const hasOpenPostPostingVoucherRef = (
  row: OpenPostPostingWrapperWithSum,
): row is Merge<
  OpenPostPostingWrapperWithSum,
  {
    openPostPosting: SetRequiredAndNonNullable<
      NonNullable<OpenPostPostingWrapperWithSum['openPostPosting']>,
      'voucherRef'
    >
  }
> => notEmpty(row.openPostPosting?.voucherRef)
